import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { renderUserOptions, showUserDisplayName } from "../../../lib/userUtils";
import {
  checkCountryCompatibility,
  checkTechLayerCompatibility,
  formatPlaceholder,
} from '../../../lib/utils';
import { getConfigurations, getFieldConfigurations } from '../../../modules/config';
import { getProjectConfig } from '../../../modules/executions';
import { selectStyle } from '../../../static-data/configDropdownOptions';
import { techLayers, automationStatuses } from '../../../static-data/DropdownOptions';
import CustomConfigCreateForm from '../../configurations/config/CustomConfigCreateForm';
import MultiSelect from '../../elements/MultiSelect';
import SkeletonLoader from '../../elements/SkeletonLoader';
import TextInput from '../../elements/TextInput';
import { updateTestSuiteCountries } from '../../../modules/testsuites';
import { formatCountryOptions } from '../../../static-data/formatCountryOptions';
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};


const CreateTestCaseForm = ({
  formData,
  setFormData,
  isEditing,
  defaultFormData,
  statuses = [],
  testSuites = [],
  priorities = [],
  modules = [],
  labels = [],
  projectUsers = [],
  loading,
  setIsFormEdited = () => { },
  isReviewWorkflow,
  refetchData,
}) => {
  const currentUserId = JSON.parse(localStorage.getItem("user")).id;
  const currentUser =
    projectUsers?.find((user) => user.id === currentUserId) || {};
  const [selectedTestSuites, setTestSuites] = useState(
    formData?.testsuites || []
  );
  const [projectConfig, setProjectConfig] = useState({});
  const [selectedCountries, setSelectedCountries] = useState(
    formData?.country || []
  );
  const hasCountryConfig =
    projectConfig?.multiple_geographies === "Yes" &&
    projectConfig?.geographies?.length > 0;
  const [selectedLabels, setSelectedLabel] = useState(formData?.labels || []);
  const [techWarning, setTechWarning] = useState(null);
  const [countryWarnings, setCountryWarnings] = useState(null);
  const [labelOptions, setLabelOptions] = useState(labels);
  const [notAutomatedCategories, setNotAutomatedCategories] = useState([]);
  const [notAutomatedSubCategories, setNotAutomatedSubCategories] = useState([]);
  const currentStatus =
    statuses.find((status) => status.id === formData.status)?.name || "";
  const { projectId } = useParams();

  useEffect(() => {
    const newTechWarning = checkTechLayerCompatibility(
      selectedTestSuites,
      formData.technologyLayer,
      testSuites
    );
    setTechWarning(newTechWarning);

    const newCountryWarnings = checkCountryCompatibility(
      selectedCountries,
      formData.testsuites,
      testSuites
    );

    setCountryWarnings(newCountryWarnings);
  }, [selectedTestSuites, selectedCountries, formData.technologyLayer]);

  const handleChangeSuite = (event) => {
    const { value } = event.target;
    const newTestSuites = typeof value === "string" ? value.split(",") : value;
    setTestSuites(newTestSuites);
    setFormData((prevFormData) => ({
      ...prevFormData,
      testsuites: newTestSuites,
    }));
    setIsFormEdited(true);
  };

  const handleCountryChange = (value) => {
    setSelectedCountries(value);
    setFormData((prevFormData) => ({
      ...prevFormData,
      country: value,
    }));
    setIsFormEdited(true);
  };

  const handleChangeLabel = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedLabel(typeof value === "string" ? value.split(",") : value);
    setFormData({
      ...formData,
      labels: typeof value === "string" ? value.split(",") : value,
    });
    setIsFormEdited(true);
  };

  const handleInputChange = useCallback(
    (id, value) => {
      const newValue = value === "" ? null : value;
      let updates = { [id]: newValue };

      if (id === "technologyLayer") {
        const warningText = checkTechLayerCompatibility(
          selectedTestSuites,
          newValue,
          testSuites
        );
        if (warningText) {
          setTechWarning(warningText);
        } else {
          setTechWarning(null);
        }
      }

      if (id === "isAutomationNeeded") {
        if (value === false) {
          updates = {
            isAutomationNeeded: false,
            isAutomated: "No",
            automationStatus: null,
            automatedBy: null,
            automationMethod: "",
          };
        } else {
          updates = {
            isAutomationNeeded: true,
            isAutomated: "No",
            automationStatus: formData.automationStatus || "Todo",
            reasonForNotAutomated: null,
            notAutomatedCategory: null,
            notAutomatedSubCategory: null,
          };
        }
      }
      if (id === "isAutomated") {
        if (value === "Yes") {
          updates = {
            isAutomated: "Yes",
            automationStatus: "Done",
            automatedBy:
              currentUserId ||
              (projectUsers.length > 0 ? projectUsers[0].id : null),
            isAutomationNeeded: true,
            reasonForNotAutomated: null,
            notAutomatedCategory: null,
            notAutomatedSubCategory: null,
          };
        } else {
          updates = {
            isAutomated: "No",
            automatedBy: null,
            automationMethod: "",
            automationStatus:
              formData.isAutomationNeeded === true ? "Todo" : null,
          };
        }
      }
      setFormData((prevData) => ({
        ...prevData,
        ...updates,
      }));
      setIsFormEdited(true);
    },
    [selectedTestSuites, setFormData]
  );

  const handleChange = useCallback(
    (e) => {
      const { id, value, type, checked } = e.target;
      setFormData((prevData) => ({
        ...prevData,
        [id]: type === "checkbox" ? checked : value,
      }));
      setIsFormEdited(true);
    },
    [setFormData]
  );

  const fetchTestCaseData = useCallback(() => {
    const updates = {};

    if (statuses && formData.status === "") {
      const defaultStatus = statuses.find((status) => status.is_default);
      if (defaultStatus) {
        updates.status = defaultStatus.id;
      }
    }

    if (priorities && formData.priority === "") {
      const defaultPriority = priorities.find(
        (priority) => priority.is_default
      );
      if (defaultPriority) {
        updates.priority = defaultPriority.id;
      }
    }

    if (projectUsers && projectUsers.length > 0) {
      if (formData.owner === "" || formData.owner === null) {
        updates.owner = currentUserId ? currentUserId : projectUsers[0].id;
      }
    }

    if (Object.keys(updates).length > 0) {
      setFormData((prevData) => ({
        ...prevData,
        ...updates,
      }));
    }
  }, []);

  const fetchDropdownData = async () => {
    try {
      const labelResponse = await getConfigurations("labels", projectId);
      const fieldResponse = await getFieldConfigurations(projectId);

      setLabelOptions(labelResponse?.data || []);

      const categories = (fieldResponse?.data || []).map(cat => ({
        ...cat,
        subCategories: cat.sub_categories || [],
      }));

      setNotAutomatedCategories(categories);

      if (formData.notAutomatedCategory) {
        const selectedCategory = categories.find(
          c => c.id === formData.notAutomatedCategory
        );
        setNotAutomatedSubCategories(selectedCategory?.subCategories || []);
      }
    } catch {
      console.log("Failed to fetch data.");
    }
  };

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const response = await getProjectConfig(projectId);
        setProjectConfig(response);
      } catch (error) {
        console.error("Error fetching project config:", error);
      }
    };
    fetchProjectData();
  }, [projectId]);

  const filterSelectedValues = (selectedValues, availableOptions) =>
    selectedValues?.filter((value) => availableOptions?.includes(value));

  useEffect(() => {
    fetchTestCaseData();
    fetchDropdownData();
    return () => {
      setFormData(defaultFormData);
      setTechWarning(null);
      setCountryWarnings(null);
    };
  }, []);

  const handleUpdateTestSuite = async () => {
    if (!countryWarnings || countryWarnings.length === 0) {
      return;
    }
    const payload = {
      updates: countryWarnings.map((warning) => ({
        suiteId: warning.testSuiteId,
        geographies: warning.countries,
      })),
    };

    try {
      await updateTestSuiteCountries(payload);
      refetchData();
      setCountryWarnings(null);
    } catch (error) {
      console.error("Failed to update test suites:", error);
    }
  };

  const handleNotAutomatedChange = (type, value) => {
    setFormData(prev => {
      if (type === 'category') {
        const category = notAutomatedCategories.find(c => c.id === value);

        setNotAutomatedSubCategories(category?.subCategories || []);

        return {
          ...prev,
          notAutomatedCategory: value,
          notAutomatedSubCategory: null,
        };
      }

      return {
        ...prev,
        notAutomatedSubCategory: value,
      };
    });

    setIsFormEdited(true);
  };


  return (
    <>
      {loading ? (
        <SkeletonLoader />
      ) : (
        <Form>
          <Form.Group as={Row} controlId="summary" className="mb-2">
            <Col>
              <Form.Label sm={3} className="required">
                Description
              </Form.Label>
              {isEditing ? (
                <>
                  <TextInput
                    id="summary"
                    required
                    value={formData.summary || ""}
                    onChange={handleInputChange}
                    placeholder="Enter description"
                    maxLength={255}
                  />
                </>
              ) : (
                <p className="form-static-text">{formData.summary}</p>
              )}
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="objective" className="mb-2">
            <Col>
              <Form.Label sm={3}>Objective</Form.Label>
              {isEditing ? (
                <TextInput
                  id="objective"
                  value={formData.objective}
                  onChange={handleInputChange}
                  maxLength={500}
                  placeholder="Enter objective"
                  type="textarea"
                  rows={3}
                />
              ) : (
                <p className="form-static-text">
                  {formData.objective || formatPlaceholder("Add an objective")}
                </p>
              )}
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="preCondition" className="mb-2">
            <Col>
              <Form.Label sm={3}>Pre-condition</Form.Label>
              {isEditing ? (
                <TextInput
                  id="preCondition"
                  value={formData.preCondition || ""}
                  onChange={handleInputChange}
                  maxLength={500}
                  placeholder="Enter pre-condition"
                  type="textarea"
                  rows={3}
                />
              ) : (
                <p className="form-static-text">
                  {formData.preCondition ||
                    formatPlaceholder("Add a pre-condition")}
                </p>
              )}
            </Col>
          </Form.Group>
          <Container fluid className="p-0">
            <Row>
              <Col>
                <h6 className="mt-3 mb-2 font-weight-bold">
                  Additional Information
                </h6>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col sm={4}>
                <Form.Group controlId="priority">
                  <Form.Label className="required">Priority</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      as="select"
                      value={formData?.priority}
                      onChange={(e) =>
                        handleInputChange("priority", e.target.value)
                      }
                      className="form-input"
                    >
                      {priorities?.map((priority) => (
                        <option key={priority.id} value={priority.id}>
                          {priority.name}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <p className="form-static-text">
                      {priorities?.find((p) => p.id === formData?.priority)
                        ?.name || ""}
                    </p>
                  )}
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group controlId="status">
                  <Form.Label className="required">Status</Form.Label>
                  {isEditing ? (
                    <>
                      <Form.Select
                        as="select"
                        value={formData.status}
                        onChange={(e) =>
                          handleInputChange("status", e.target.value)
                        }
                        className="form-input"
                      >
                        {statuses?.map((status) => {
                          const isApproved = status.name === "Approved";
                          const isMember = currentUser?.role === "member";
                          const notReviewer =
                            formData?.reviewer !== currentUserId;

                          const disableApproved =
                            isReviewWorkflow &&
                            isApproved &&
                            isMember &&
                            notReviewer;

                          return (
                            <option
                              key={status.id}
                              value={status.id}
                              disabled={disableApproved}
                            >
                              {status.name}
                            </option>
                          );
                        })}
                      </Form.Select>

                      {currentStatus === "Archived" && (
                        <Typography variant="caption" color="error">
                          *Archiving test case will remove it from all test
                          suites. It cannot be linked to test cycles or
                          executed.
                        </Typography>
                      )}
                      {isReviewWorkflow && currentUser?.role === "member" && (
                        <Typography variant="caption" color="textSecondary">
                          *Only assigned reviewers or admins can approve test
                          cases.
                        </Typography>
                      )}
                    </>
                  ) : (
                    <p className="form-static-text">
                      {currentStatus || ""}{" "}
                      {currentStatus === "Approved" && (
                        <span
                          className="text-muted"
                          style={{ fontSize: "12px" }}
                        >
                          (By:{" "}
                          {projectUsers.find(
                            (user) => user.id === formData.approvedBy
                          )?.full_name || ""}
                          )
                        </span>
                      )}
                    </p>
                  )}
                </Form.Group>
              </Col>
              {isReviewWorkflow && currentStatus === "In Review" && (
                <Col sm={4}>
                  <Form.Group controlId="reviewer">
                    <Form.Label className="required">Reviewer</Form.Label>
                    {isEditing ? (
                      <Form.Select
                        as="select"
                        value={
                          projectUsers?.some(
                            (user) => user.id === formData.reviewer
                          )
                            ? formData.reviewer
                            : ""
                        }
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="" disabled>
                          None
                        </option>
                        {renderUserOptions(projectUsers)}
                      </Form.Select>
                    ) : (
                      <p className="form-static-text">
                        {showUserDisplayName(formData?.reviewer, projectUsers)}
                      </p>
                    )}
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Row className="mt-2">
              <Col sm={4}>
                <Form.Group controlId="labels">
                  <Form.Label>Labels</Form.Label>
                  {isEditing ? (
                    <>
                      <FormControl sx={{ width: "100%", p: 0 }}>
                        <Select
                          labelId="test-case-labels"
                          id="labels"
                          multiple
                          sx={selectStyle}
                          IconComponent={KeyboardArrowDownIcon}
                          value={selectedLabels}
                          onChange={handleChangeLabel}
                          input={<OutlinedInput placeholder="Labels" />}
                          renderValue={
                            selectedLabels?.length === 0
                              ? () => "None"
                              : (selected) => {
                                return selected
                                  .map(
                                    (id) =>
                                      labelOptions?.find(
                                        (label) => label.id === id
                                      )?.name
                                  )
                                  .join(", ");
                              }
                          }
                          MenuProps={MenuProps}
                        >
                          {labelOptions?.length > 0 ? (
                            labelOptions?.map((label) => (
                              <MenuItem key={label.id} value={label.id}>
                                <Checkbox
                                  disabled={
                                    !selectedLabels?.includes(label.id) &&
                                    selectedLabels.length === 3
                                  }
                                  disableRipple
                                  checked={selectedLabels.includes(label.id)}
                                />
                                <ListItemText
                                  sx={{
                                    whiteSpace: "break-spaces",
                                  }}
                                  primary={<span>{label.name}</span>}
                                />
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled>
                              <ListItemText primary="No labels found" />
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                      <CustomConfigCreateForm
                        configType="labels"
                        fetchDropdownData={fetchDropdownData}
                      />
                    </>
                  ) : (
                    <p className="form-static-text">
                      {formData.labels && formData.labels.length > 0
                        ? labels
                          .filter(
                            (label) => formData.labels.indexOf(label.id) > -1
                          )
                          ?.map((ele) => ele.name)
                          .join(", ")
                        : "None"}
                    </p>
                  )}
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group controlId="owner">
                  <Form.Label className="required">Author</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      as="select"
                      value={
                        projectUsers?.some((user) => user.id === formData.owner)
                          ? formData.owner
                          : ""
                      }
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="" disabled>
                        None
                      </option>
                      {renderUserOptions(projectUsers)}
                    </Form.Select>
                  ) : (
                    <p className="form-static-text">
                      {showUserDisplayName(formData?.owner, projectUsers)}
                    </p>
                  )}
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group controlId="module">
                  <Form.Label>Module</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      as="select"
                      value={formData.module || ""}
                      onChange={(e) =>
                        handleInputChange("module", e.target.value)
                      }
                      className="form-input"
                    >
                      <option value="">None</option>
                      {modules?.map((module) => (
                        <option key={module.id} value={module.id}>
                          {module.name}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <p className="form-static-text">
                      {modules?.find((s) => s.id === formData.module)?.name ||
                        "None"}
                    </p>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col sm={4}>
                <Form.Group controlId="technologyLayer">
                  <Form.Label className="required">Technology Layer</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      as="select"
                      value={formData.technologyLayer || ""}
                      onChange={(e) =>
                        handleInputChange("technologyLayer", e.target.value)
                      }
                      className="form-input"
                    >
                      {techLayers?.map((technologyLayer) => (
                        <option
                          key={technologyLayer}
                          value={
                            technologyLayer === "None" ? "" : technologyLayer
                          }
                          disabled={technologyLayer === "None"}
                        >
                          {technologyLayer}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <p className="form-static-text">
                      {formData.technologyLayer || "None"}
                    </p>
                  )}
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group controlId="testsuites">
                  <Form.Label className="required">Test Suite</Form.Label>
                  {isEditing ? (
                    <FormControl sx={{ width: "100%", p: 0 }}>
                      <Select
                        labelId="test-suites-label"
                        id="test-suites"
                        multiple
                        sx={{
                          maxHeight: "48px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ced4da",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ced4da",
                          },
                        }}
                        IconComponent={KeyboardArrowDownIcon}
                        value={selectedTestSuites}
                        onChange={handleChangeSuite}
                        input={<OutlinedInput placeholder="Test Suites" />}
                        renderValue={
                          selectedTestSuites?.length === 0
                            ? () => "None"
                            : (selected) => {
                              return selected
                                .map(
                                  (id) =>
                                    testSuites.find(
                                      (suite) => suite.id === id
                                    )?.name
                                )
                                .join(", ");
                            }
                        }
                        MenuProps={MenuProps}
                      >
                        {testSuites?.map((suite) => (
                          <MenuItem key={suite.name} value={suite.id} disabled={suite.status === "suspended"}>
                            <Checkbox
                              disableRipple
                              checked={selectedTestSuites.includes(suite.id)}
                              disabled={suite.status === "suspended"}
                            />
                            <ListItemText
                              sx={{
                                whiteSpace: "break-spaces",
                              }}
                              primary={
                                <span>
                                  {suite.name}{" "}
                                  <span
                                    style={{ fontSize: "10px", color: "red" }}
                                  >
                                    {suite.status === "suspended" &&
                                      "(Suspended)"}
                                  </span>
                                </span>
                              }
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <p className="form-static-text">
                      {formData.testsuites && formData.testsuites.length > 0
                        ? testSuites
                          .filter(
                            (suite) =>
                              formData.testsuites.indexOf(suite.id) > -1
                          )
                          ?.map((ele) => ele.name)
                          .join(", ")
                        : "None"}
                    </p>
                  )}
                </Form.Group>
              </Col>
              {hasCountryConfig && (
                <Col md={4}>
                  <Form.Group controlId="geographies">
                    <Form.Label>Countries</Form.Label>
                    {isEditing ? (
                      (() => {
                        const formatted = formatCountryOptions(
                          projectConfig.geographies
                        );
                        const displayMap = {};
                        formatted.forEach((item) => {
                          displayMap[item.value] = item.display;
                        });
                        return (
                          <MultiSelect
                            options={projectConfig?.geographies}
                            selectedValues={filterSelectedValues(
                              selectedCountries,
                              projectConfig?.geographies
                            )}
                            onChange={(value) => handleCountryChange(value)}
                            placeholder="Select countries"
                            renderOption={(option) => displayMap[option]}
                          />
                        );
                      })()
                    ) : (
                      <p className="form-static-text">
                        {filterSelectedValues(
                          formData?.country,
                          projectConfig?.geographies
                        )?.length > 0
                          ? filterSelectedValues(
                            formData?.country,
                            projectConfig?.geographies
                          ).join(", ")
                          : "None"}
                      </p>
                    )}
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Row>
              <Col>
                <h6 className="mt-4 mb-3 font-weight-bold">
                  Automation Details
                </h6>
              </Col>
            </Row>
            <Row>
              <Col sm={4}>
                <Form.Group controlId="isAutomationNeeded">
                  <Form.Label>Candidate for Automation</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      as="select"
                      value={
                        formData.isAutomationNeeded === true ? "Yes" : "No"
                      }
                      onChange={(e) =>
                        handleInputChange(
                          "isAutomationNeeded",
                          e.target.value === "Yes"
                        )
                      }
                      className="form-input"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Form.Select>
                  ) : (
                    <p className="form-static-text">
                      {formData.isAutomationNeeded ? "Yes" : "No"}
                    </p>
                  )}
                </Form.Group>
              </Col>
              {formData.isAutomationNeeded === true && (
                <>
                  <Col sm={4}>
                    <Form.Group controlId="automationStatus">
                      <Form.Label>Automation Status</Form.Label>
                      {isEditing ? (
                        <Form.Select
                          as="select"
                          value={formData.automationStatus || "Todo"}
                          onChange={(e) =>
                            handleInputChange(
                              "automationStatus",
                              e.target.value
                            )
                          }
                          className="form-input"
                          disabled={
                            !formData.isAutomationNeeded ||
                            formData.isAutomated === "Yes"
                          }
                        >
                          {automationStatuses.map((status) => (
                            <option
                              key={status}
                              value={status}
                              disabled={status === "Done"}
                            >
                              {status}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <p className="form-static-text">
                          {formData.automationStatus}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col sm={4}>
                    <Form.Group controlId="isAutomated">
                      <Form.Label>Is Automated?</Form.Label>
                      {isEditing ? (
                        <Form.Select
                          as="select"
                          value={formData.isAutomated || "No"}
                          onChange={(e) =>
                            handleInputChange("isAutomated", e.target.value)
                          }
                          className="form-input"
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </Form.Select>
                      ) : (
                        <p className="form-static-text">
                          {formData.isAutomated}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
            {(formData.isAutomationNeeded === false ||
              formData.isAutomated === "No") && (
                <>
                  <Row className="mt-3">
                    <Form.Label sm={4}>Reason for not automating</Form.Label>
                    <Col sm={4}>
                      <Form.Group controlId="notAutomatedCategory">
                        <Form.Label className={formData.isAutomationNeeded === true && formData.isAutomated === "No" ? "required" : ""}>Category</Form.Label>
                        {isEditing ? (
                          <Form.Select
                            value={formData.notAutomatedCategory || ""}
                            onChange={(e) =>
                              handleNotAutomatedChange('category', e.target.value)
                            }
                            className="form-input"
                          >
                            <option value="" disabled>None</option>
                            {notAutomatedCategories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <p className="form-static-text">
                            {
                              notAutomatedCategories.find(
                                c => c.id === formData.notAutomatedCategory
                              )?.name || "None"
                            }
                          </p>
                        )}
                      </Form.Group>

                    </Col>
                    <Col sm={4}>
                      <Form.Group controlId="notAutomatedSubCategory">
                        <Form.Label className={formData.isAutomationNeeded === true && formData.isAutomated === "No" ? "required" : ""}>Sub Category</Form.Label>
                        {isEditing ? (
                          <Form.Select
                            value={formData.notAutomatedSubCategory || ""}
                            onChange={(e) =>
                              handleNotAutomatedChange('subcategory', e.target.value)
                            }
                            className="form-input"
                            disabled={!notAutomatedSubCategories.length || !formData.notAutomatedCategory || formData.notAutomatedCategory === ""}
                          >
                            <option value="" disabled>None</option>
                            {notAutomatedSubCategories.map((sub) => (
                              <option key={sub.id} value={sub.id}>
                                {sub.name}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <p className="form-static-text">
                            {
                              notAutomatedSubCategories.find(
                                s => s.id === formData.notAutomatedSubCategory
                              )?.name || "None"
                            }
                          </p>
                        )}
                      </Form.Group>

                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col>
                      <Form.Group
                        controlId="reasonForNotAutomated"
                        className="mb-2"
                      >
                        <Form.Label sm={4}>Reason</Form.Label>
                        {isEditing ? (
                          <TextInput
                            id="reasonForNotAutomated"
                            value={formData.reasonForNotAutomated}
                            onChange={handleInputChange}
                            maxLength={255}
                            placeholder="Enter reason"
                            type="textarea"
                            rows={2}
                          />
                        ) : (
                          <p className="form-static-text">
                            {formData.reasonForNotAutomated || formatPlaceholder()}
                          </p>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
            {formData.isAutomationNeeded === true &&
              formData.isAutomated === "Yes" && (
                <Row className="mt-4">
                  <Col sm={4}>
                    <Form.Group controlId="automatedBy">
                      <Form.Label className="required">Automated By</Form.Label>
                      {isEditing ? (
                        <Form.Select
                          as="select"
                          value={
                            projectUsers?.some(
                              (user) => user.id === formData.automatedBy
                            )
                              ? formData.automatedBy
                              : ""
                          }
                          onChange={handleChange}
                          className="form-input"
                        >
                          <option value="" disabled>
                            None
                          </option>
                          {renderUserOptions(projectUsers)}
                        </Form.Select>
                      ) : (
                        <p className="form-static-text">
                          {showUserDisplayName(
                            formData?.automatedBy,
                            projectUsers
                          )}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group controlId="automationMethod" className="mb-2">
                      <Form.Label sm={4} className="required">
                        Automation Method
                      </Form.Label>
                      <Col>
                        {isEditing ? (
                          <>
                            <TextInput
                              id="automationMethod"
                              required
                              value={formData.automationMethod || ""}
                              onChange={handleInputChange}
                              placeholder="Enter automation method"
                              maxLength={100}
                            />
                          </>
                        ) : (
                          <p className="form-static-text">
                            {formData.automationMethod}
                          </p>
                        )}
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              )}
            {formData.isAutomationNeeded === true &&
              formData.isAutomated === "Yes" &&
              hasCountryConfig && (
                <Row className="mt-1">
                  <Col sm={4}>
                    <Form.Group controlId="automationCountry">
                      <Form.Label>Automated for country</Form.Label>
                      {isEditing ? (
                        (() => {
                          const availableCountry =
                            formData?.country?.length > 0
                              ? formData.country
                              : [];
                          const formatted =
                            formatCountryOptions(availableCountry);
                          const displayMap = {};
                          formatted.forEach((item) => {
                            displayMap[item.value] = item.display;
                          });
                          return (
                            <>
                              <MultiSelect
                                sx={{ mb: 0 }}
                                options={availableCountry}
                                selectedValues={filterSelectedValues(
                                  formData?.automationCountry || [],
                                  availableCountry
                                )}
                                onChange={(value) => {
                                  setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    automationCountry: value,
                                  }));
                                  setIsFormEdited(true);
                                }}
                                placeholder="Select countries"
                                disabled={
                                  availableCountry.length > 0 ? false : true
                                }
                                renderOption={(option) => displayMap[option]}
                              />
                              {availableCountry.length === 0 && (
                                <Typography variant="caption" color="error">
                                  Please select countries for test case first.
                                </Typography>
                              )}
                            </>
                          );
                        })()
                      ) : (
                        <p className="form-static-text">
                          {formData?.automationCountry?.length > 0
                            ? filterSelectedValues(
                              formData.automationCountry,
                              projectConfig?.geographies
                            ).join(", ")
                            : "None"}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              )}
            {techWarning && (
              <Alert severity="warning" className="mt-3">
                {techWarning && <p className="font-semibold">{techWarning}</p>}
              </Alert>
            )}
            {countryWarnings && (
              <Alert severity="warning" className="mt-2">
                <p>
                  Some of the selected countries are not available in the
                  selected test suites:
                </p>
                {countryWarnings.map((warning, index) => {
                  const countryList =
                    warning.countries.length > 2
                      ? warning.countries.join(", ")
                      : warning.countries.join(" & ");
                  return (
                    <div key={index} className="mb-2 last:mb-0">
                      {`${countryList} are missing from the test suite`}
                      <strong> {warning.testSuiteName}</strong>
                    </div>
                  );
                })}
                <p>
                  Would you like to update the test suites to include them?{" "}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleUpdateTestSuite}
                  >
                    Update
                  </Button>
                </p>
              </Alert>
            )}
          </Container>
        </Form>
      )}
    </>
  );
};

export default CreateTestCaseForm;
